'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';


// ─── Types ─────────────────────────────────────────────────────────────────────
type FilterPeriod = 'weekly' | 'monthly' | 'global';

interface StreakEntry {
  rank: number;
  user_id: string;
  display_name: string;
  avatar_initials: string;
  current_streak: number;
  longest_streak: number;
  prayers_completed: number;
  badge: 'flame' | 'star' | 'moon' | 'sun';
  is_current_user?: boolean;
}

interface HabitEntry {
  rank: number;
  user_id: string;
  display_name: string;
  avatar_initials: string;
  consistency_pct: number;
  habits_completed: number;
  total_habits: number;
  is_current_user?: boolean;
}

interface CompanionEntry {
  user_id: string;
  display_name: string;
  avatar_initials: string;
  shared_prayers: number;
  streak_together: number;
  last_active: string;
}

// ─── Mock Data ─────────────────────────────────────────────────────────────────
const MOCK_STREAKS: StreakEntry[] = [
  { rank: 1, user_id: 'u1', display_name: 'Fatima Al-Rashid', avatar_initials: 'FA', current_streak: 142, longest_streak: 142, prayers_completed: 710, badge: 'flame' },
  { rank: 2, user_id: 'u2', display_name: 'Omar Hussain', avatar_initials: 'OH', current_streak: 98, longest_streak: 120, prayers_completed: 490, badge: 'flame' },
  { rank: 3, user_id: 'u3', display_name: 'Aisha Noor', avatar_initials: 'AN', current_streak: 87, longest_streak: 87, prayers_completed: 435, badge: 'star' },
  { rank: 4, user_id: 'u4', display_name: 'Yusuf Ibrahim', avatar_initials: 'YI', current_streak: 74, longest_streak: 90, prayers_completed: 370, badge: 'star' },
  { rank: 5, user_id: 'u5', display_name: 'Maryam Khalid', avatar_initials: 'MK', current_streak: 61, longest_streak: 75, prayers_completed: 305, badge: 'moon' },
  { rank: 6, user_id: 'u6', display_name: 'Hassan Al-Amin', avatar_initials: 'HA', current_streak: 55, longest_streak: 68, prayers_completed: 275, badge: 'moon' },
  { rank: 7, user_id: 'u7', display_name: 'Zainab Osman', avatar_initials: 'ZO', current_streak: 43, longest_streak: 55, prayers_completed: 215, badge: 'sun' },
  { rank: 8, user_id: 'u8', display_name: 'Bilal Siddiqui', avatar_initials: 'BS', current_streak: 38, longest_streak: 50, prayers_completed: 190, badge: 'sun' },
  { rank: 9, user_id: 'u9', display_name: 'Khadija Musa', avatar_initials: 'KM', current_streak: 29, longest_streak: 40, prayers_completed: 145, badge: 'sun' },
  { rank: 10, user_id: 'u10', display_name: 'Idris Abdi', avatar_initials: 'IA', current_streak: 21, longest_streak: 35, prayers_completed: 105, badge: 'sun' },
];

const MOCK_HABITS: HabitEntry[] = [
  { rank: 1, user_id: 'u3', display_name: 'Aisha Noor', avatar_initials: 'AN', consistency_pct: 98, habits_completed: 196, total_habits: 200 },
  { rank: 2, user_id: 'u1', display_name: 'Fatima Al-Rashid', avatar_initials: 'FA', consistency_pct: 95, habits_completed: 190, total_habits: 200 },
  { rank: 3, user_id: 'u5', display_name: 'Maryam Khalid', avatar_initials: 'MK', consistency_pct: 92, habits_completed: 184, total_habits: 200 },
  { rank: 4, user_id: 'u2', display_name: 'Omar Hussain', avatar_initials: 'OH', consistency_pct: 88, habits_completed: 176, total_habits: 200 },
  { rank: 5, user_id: 'u6', display_name: 'Hassan Al-Amin', avatar_initials: 'HA', consistency_pct: 84, habits_completed: 168, total_habits: 200 },
  { rank: 6, user_id: 'u4', display_name: 'Yusuf Ibrahim', avatar_initials: 'YI', consistency_pct: 79, habits_completed: 158, total_habits: 200 },
  { rank: 7, user_id: 'u8', display_name: 'Bilal Siddiqui', avatar_initials: 'BS', consistency_pct: 74, habits_completed: 148, total_habits: 200 },
  { rank: 8, user_id: 'u7', display_name: 'Zainab Osman', avatar_initials: 'ZO', consistency_pct: 70, habits_completed: 140, total_habits: 200 },
];

const MOCK_COMPANIONS: CompanionEntry[] = [
  { user_id: 'c1', display_name: 'Fatima Al-Rashid', avatar_initials: 'FA', shared_prayers: 284, streak_together: 38, last_active: '2026-07-03T08:00:00Z' },
  { user_id: 'c2', display_name: 'Omar Hussain', avatar_initials: 'OH', shared_prayers: 196, streak_together: 27, last_active: '2026-07-03T06:30:00Z' },
  { user_id: 'c3', display_name: 'Aisha Noor', avatar_initials: 'AN', shared_prayers: 152, streak_together: 21, last_active: '2026-07-02T20:00:00Z' },
  { user_id: 'c4', display_name: 'Yusuf Ibrahim', avatar_initials: 'YI', shared_prayers: 98, streak_together: 14, last_active: '2026-07-02T18:45:00Z' },
  { user_id: 'c5', display_name: 'Maryam Khalid', avatar_initials: 'MK', shared_prayers: 74, streak_together: 10, last_active: '2026-07-01T21:00:00Z' },
];

// ─── Badge Config ──────────────────────────────────────────────────────────────
const BADGE_CONFIG = {
  flame: { emoji: '🔥', label: 'On Fire', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  star:  { emoji: '⭐', label: 'Star',    bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200' },
  moon:  { emoji: '🌙', label: 'Devoted', bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  sun:   { emoji: '☀️', label: 'Rising',  bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
};

const RANK_MEDALS: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };

// ─── Helpers ───────────────────────────────────────────────────────────────────
function timeAgo(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function getConsistencyColor(pct: number): string {
  if (pct >= 90) return 'bg-emerald-500';
  if (pct >= 75) return 'bg-teal-500';
  if (pct >= 60) return 'bg-amber-500';
  return 'bg-rose-400';
}

// ─── Sub-components ────────────────────────────────────────────────────────────
function FilterTabs({ active, onChange }: { active: FilterPeriod; onChange: (p: FilterPeriod) => void }) {
  const tabs: { key: FilterPeriod; label: string; icon: string }[] = [
    { key: 'weekly',  label: 'This Week',  icon: '📅' },
    { key: 'monthly', label: 'This Month', icon: '🗓️' },
    { key: 'global',  label: 'All Time',   icon: '🌍' },
  ];
  return (
    <div className="flex items-center bg-muted rounded-xl p-1 gap-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-600 transition-all duration-150 ${
            active === t.key
              ? 'bg-card text-foreground shadow-sm border border-border'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <span>{t.icon}</span>
          <span className="hidden sm:inline">{t.label}</span>
        </button>
      ))}
    </div>
  );
}

function TopStreakPodium({ entries }: { entries: StreakEntry[] }) {
  const top3 = entries.slice(0, 3);
  // Reorder: 2nd, 1st, 3rd for podium effect
  const podiumOrder = [top3[1], top3[0], top3[2]].filter(Boolean);
  const heights = [top3[1] ? 'pt-6' : '', '', 'pt-10'];

  return (
    <div className="flex items-end justify-center gap-3 mb-8">
      {podiumOrder.map((entry, idx) => {
        const isCenter = entry.rank === 1;
        const badge = BADGE_CONFIG[entry.badge];
        return (
          <div
            key={entry.user_id}
            className={`flex flex-col items-center ${idx === 0 ? 'pt-6' : idx === 2 ? 'pt-10' : ''}`}
          >
            <div className="text-2xl mb-1">{RANK_MEDALS[entry.rank]}</div>
            <div
              className={`relative flex flex-col items-center p-4 rounded-2xl bg-card border border-border transition-transform duration-200 hover:-translate-y-1 ${
                isCenter
                  ? 'shadow-[0_0_24px_rgba(251,191,36,0.3)] scale-105 z-10 min-w-[140px]'
                  : 'min-w-[120px] shadow-card'
              }`}
            >
              <div className={`w-14 h-14 rounded-2xl gradient-teal flex items-center justify-center text-white font-700 text-lg mb-2 shadow-prayer`}>
                {entry.avatar_initials}
              </div>
              <p className="font-700 text-foreground text-xs text-center leading-tight mb-1 truncate w-full text-center">
                {entry.display_name}
              </p>
              <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-600 border ${badge.bg} ${badge.text} ${badge.border} mb-2`}>
                {badge.emoji} {entry.current_streak}d
              </div>
              <p className="text-xs text-muted-foreground">{entry.prayers_completed} prayers</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StreakRow({ entry }: { entry: StreakEntry }) {
  const badge = BADGE_CONFIG[entry.badge];
  const medal = RANK_MEDALS[entry.rank];
  return (
    <div className={`flex items-center gap-3 px-5 py-3.5 hover:bg-muted/30 transition-colors duration-100 ${entry.is_current_user ? 'bg-primary/5 border-l-2 border-primary' : ''}`}>
      <div className="w-7 text-center flex-shrink-0">
        {medal ? (
          <span className="text-lg">{medal}</span>
        ) : (
          <span className="text-sm font-700 text-muted-foreground">{entry.rank}</span>
        )}
      </div>
      <div className="w-9 h-9 rounded-xl gradient-teal flex items-center justify-center text-white font-700 text-xs flex-shrink-0">
        {entry.avatar_initials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-600 text-foreground truncate">
          {entry.display_name}
          {entry.is_current_user && <span className="ml-1.5 text-xs text-primary font-500">(you)</span>}
        </p>
        <p className="text-xs text-muted-foreground">{entry.prayers_completed} prayers completed</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-600 border ${badge.bg} ${badge.text} ${badge.border}`}>
          {badge.emoji} {entry.current_streak} days
        </span>
      </div>
    </div>
  );
}

function HabitRow({ entry }: { entry: HabitEntry }) {
  const medal = RANK_MEDALS[entry.rank];
  const barColor = getConsistencyColor(entry.consistency_pct);
  return (
    <div className={`flex items-center gap-3 px-5 py-3.5 hover:bg-muted/30 transition-colors duration-100 ${entry.is_current_user ? 'bg-primary/5 border-l-2 border-primary' : ''}`}>
      <div className="w-7 text-center flex-shrink-0">
        {medal ? (
          <span className="text-lg">{medal}</span>
        ) : (
          <span className="text-sm font-700 text-muted-foreground">{entry.rank}</span>
        )}
      </div>
      <div className="w-9 h-9 rounded-xl gradient-teal flex items-center justify-center text-white font-700 text-xs flex-shrink-0">
        {entry.avatar_initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-600 text-foreground truncate">
            {entry.display_name}
            {entry.is_current_user && <span className="ml-1.5 text-xs text-primary font-500">(you)</span>}
          </p>
          <span className="text-sm font-700 text-foreground ml-2 flex-shrink-0">{entry.consistency_pct}%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${barColor} transition-all duration-700`}
              style={{ width: `${entry.consistency_pct}%` }}
            />
          </div>
          <span className="text-xs text-muted-foreground flex-shrink-0">{entry.habits_completed}/{entry.total_habits}</span>
        </div>
      </div>
    </div>
  );
}

function CompanionCard({ companion }: { companion: CompanionEntry }) {
  return (
    <div className="flex items-center gap-3 p-4 bg-card border border-border rounded-2xl hover:border-primary/30 hover:shadow-card transition-all duration-200">
      <div className="w-11 h-11 rounded-2xl gradient-teal flex items-center justify-center text-white font-700 text-sm flex-shrink-0 shadow-prayer">
        {companion.avatar_initials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-600 text-foreground truncate">{companion.display_name}</p>
        <p className="text-xs text-muted-foreground">{timeAgo(companion.last_active)}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="flex items-center gap-1 justify-end mb-0.5">
          <span className="text-sm">🤝</span>
          <span className="text-sm font-700 text-foreground">{companion.shared_prayers}</span>
        </div>
        <p className="text-xs text-muted-foreground">{companion.streak_together}d together</p>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function PrayerLeaderboardPage() {
  const { user } = useAuth();
  const [filter, setFilter] = useState<FilterPeriod>('global');
  const [activeTab, setActiveTab] = useState<'streaks' | 'habits' | 'companions'>('streaks');
  const [streaks, setStreaks] = useState<StreakEntry[]>([]);
  const [habits, setHabits] = useState<HabitEntry[]>([]);
  const [companions, setCompanions] = useState<CompanionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRank, setUserRank] = useState<{ streak: number | null; habit: number | null }>({ streak: null, habit: null });

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const supabase = createClient();

        // Try to fetch real streak data
        const { data: streakData, error: streakError } = await supabase
          .from('user_streaks')
          .select('user_id, current_streak, longest_streak, prayers_completed_total')
          .order('current_streak', { ascending: false })
          .limit(20);

        if (!streakError && streakData && streakData.length > 0) {
          const userIds = streakData.map((d: { user_id: string }) => d.user_id);
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('id, full_name')
            .in('id', userIds);

          const nameMap: Record<string, string> = {};
          (profiles || []).forEach((p: { id: string; full_name: string }) => {
            nameMap[p.id] = p.full_name || 'Anonymous';
          });

          const mapped: StreakEntry[] = streakData.map((d: {
            user_id: string;
            current_streak: number;
            longest_streak: number;
            prayers_completed_total: number;
          }, idx: number) => {
            const streak = d.current_streak || 0;
            const badge: StreakEntry['badge'] = streak >= 100 ? 'flame' : streak >= 60 ? 'star' : streak >= 30 ? 'moon' : 'sun';
            const name = nameMap[d.user_id] || 'Anonymous';
            return {
              rank: idx + 1,
              user_id: d.user_id,
              display_name: name,
              avatar_initials: name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase(),
              current_streak: streak,
              longest_streak: d.longest_streak || streak,
              prayers_completed: d.prayers_completed_total || 0,
              badge,
              is_current_user: d.user_id === user?.id,
            };
          });
          setStreaks(mapped);

          const myStreakRank = mapped.findIndex((e) => e.user_id === user?.id);
          setUserRank((prev) => ({ ...prev, streak: myStreakRank >= 0 ? myStreakRank + 1 : null }));
        } else {
          // Mark current user in mock data
          const mockWithUser = MOCK_STREAKS.map((e, idx) => ({
            ...e,
            is_current_user: idx === 4, // highlight 5th as "you" for demo
          }));
          setStreaks(mockWithUser);
          setUserRank((prev) => ({ ...prev, streak: 5 }));
        }

        // Habits data
        const { data: habitData, error: habitError } = await supabase
          .from('user_habit_stats')
          .select('user_id, consistency_pct, habits_completed, total_habits')
          .order('consistency_pct', { ascending: false })
          .limit(20);

        if (!habitError && habitData && habitData.length > 0) {
          const userIds2 = habitData.map((d: { user_id: string }) => d.user_id);
          const { data: profiles2 } = await supabase
            .from('user_profiles')
            .select('id, full_name')
            .in('id', userIds2);

          const nameMap2: Record<string, string> = {};
          (profiles2 || []).forEach((p: { id: string; full_name: string }) => {
            nameMap2[p.id] = p.full_name || 'Anonymous';
          });

          const mappedHabits: HabitEntry[] = habitData.map((d: {
            user_id: string;
            consistency_pct: number;
            habits_completed: number;
            total_habits: number;
          }, idx: number) => {
            const name = nameMap2[d.user_id] || 'Anonymous';
            return {
              rank: idx + 1,
              user_id: d.user_id,
              display_name: name,
              avatar_initials: name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase(),
              consistency_pct: d.consistency_pct || 0,
              habits_completed: d.habits_completed || 0,
              total_habits: d.total_habits || 1,
              is_current_user: d.user_id === user?.id,
            };
          });
          setHabits(mappedHabits);
          const myHabitRank = mappedHabits.findIndex((e) => e.user_id === user?.id);
          setUserRank((prev) => ({ ...prev, habit: myHabitRank >= 0 ? myHabitRank + 1 : null }));
        } else {
          const mockHabitsWithUser = MOCK_HABITS.map((e, idx) => ({
            ...e,
            is_current_user: idx === 3,
          }));
          setHabits(mockHabitsWithUser);
          setUserRank((prev) => ({ ...prev, habit: 4 }));
        }

        setCompanions(MOCK_COMPANIONS);
      } catch {
        setStreaks(MOCK_STREAKS);
        setHabits(MOCK_HABITS);
        setCompanions(MOCK_COMPANIONS);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [filter, user?.id]);

  const topStreaks = streaks.slice(0, 3);
  const restStreaks = streaks.slice(3);

  const communityStats = [
    { label: 'Active Streaks', value: streaks.length + '+ users', icon: '🔥' },
    { label: 'Avg Consistency', value: habits.length > 0 ? Math.round(habits.reduce((s, h) => s + h.consistency_pct, 0) / habits.length) + '%' : '—', icon: '📊' },
    { label: 'Prayer Companions', value: companions.length + ' active', icon: '🤝' },
    { label: 'Top Streak', value: streaks[0] ? streaks[0].current_streak + ' days' : '—', icon: '⭐' },
  ];

  return (
    <AppLayout activeRoute="/prayer-leaderboard">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">

        {/* ─── Header ─────────────────────────────────────────────────────── */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">🏆</span>
            <h1 className="text-2xl font-700 text-foreground">Community Rankings</h1>
          </div>
          <p className="text-muted-foreground text-sm">
            Top prayer streaks, habit consistency, and companions driving community growth.
          </p>
        </div>

        {/* ─── Your Rank Banner ────────────────────────────────────────────── */}
        {(userRank.streak || userRank.habit) && (
          <div className="mb-6 p-4 rounded-2xl bg-primary/5 border border-primary/20 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-xl">🎯</span>
              <span className="text-sm font-600 text-foreground">Your Rankings</span>
            </div>
            {userRank.streak && (
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-600">
                🔥 Streak Rank #{userRank.streak}
              </div>
            )}
            {userRank.habit && (
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-xs font-600">
                📊 Habit Rank #{userRank.habit}
              </div>
            )}
            <Link href="/my-progress" className="ml-auto text-xs font-600 text-primary hover:underline">
              View my progress →
            </Link>
          </div>
        )}

        {/* ─── Community Stats ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {communityStats.map((stat) => (
            <div key={stat.label} className="bg-card border border-border rounded-2xl p-4 text-center shadow-card">
              <div className="text-2xl mb-1">{stat.icon}</div>
              <p className="text-base font-700 text-foreground">{stat.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* ─── Filter + Tab Controls ───────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          {/* Tab selector */}
          <div className="flex items-center bg-muted rounded-xl p-1 gap-1">
            {([
              { key: 'streaks',    label: 'Prayer Streaks',  icon: '🔥' },
              { key: 'habits',     label: 'Habit Consistency', icon: '📊' },
              { key: 'companions', label: 'Companions',       icon: '🤝' },
            ] as { key: typeof activeTab; label: string; icon: string }[]).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-600 transition-all duration-150 ${
                  activeTab === tab.key
                    ? 'bg-card text-foreground shadow-sm border border-border'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <span>{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Period filter (not shown for companions) */}
          {activeTab !== 'companions' && (
            <FilterTabs active={filter} onChange={setFilter} />
          )}
        </div>

        {/* ─── Content ─────────────────────────────────────────────────────── */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-muted-foreground">Loading rankings…</p>
            </div>
          </div>
        ) : (
          <>
            {/* ── Prayer Streaks ── */}
            {activeTab === 'streaks' && (
              <div>
                {/* Podium */}
                {topStreaks.length >= 3 && (
                  <TopStreakPodium entries={topStreaks} />
                )}

                {/* Full list */}
                <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <h2 className="font-700 text-foreground text-sm">Top Prayer Streaks</h2>
                    <span className="text-xs text-muted-foreground capitalize">{filter === 'global' ? 'All Time' : filter === 'monthly' ? 'This Month' : 'This Week'}</span>
                  </div>
                  <div className="divide-y divide-border">
                    {streaks.map((entry) => (
                      <StreakRow key={entry.user_id} entry={entry} />
                    ))}
                  </div>
                  {streaks.length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-3xl mb-2">🔥</p>
                      <p className="text-sm text-muted-foreground">No streak data yet. Start praying consistently!</p>
                    </div>
                  )}
                </div>

                {/* Badge legend */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {(Object.entries(BADGE_CONFIG) as [StreakEntry['badge'], typeof BADGE_CONFIG[StreakEntry['badge']]][]).map(([key, cfg]) => (
                    <div key={key} className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-500 border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                      {cfg.emoji} {cfg.label}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Habit Consistency ── */}
            {activeTab === 'habits' && (
              <div>
                <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <h2 className="font-700 text-foreground text-sm">Habit Consistency Rankings</h2>
                    <span className="text-xs text-muted-foreground capitalize">{filter === 'global' ? 'All Time' : filter === 'monthly' ? 'This Month' : 'This Week'}</span>
                  </div>
                  <div className="divide-y divide-border">
                    {habits.map((entry) => (
                      <HabitRow key={entry.user_id} entry={entry} />
                    ))}
                  </div>
                  {habits.length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-3xl mb-2">📊</p>
                      <p className="text-sm text-muted-foreground">No habit data yet. Track your daily habits!</p>
                    </div>
                  )}
                </div>

                {/* Consistency legend */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {[
                    { label: '90%+ Excellent', color: 'bg-emerald-500' },
                    { label: '75%+ Good',      color: 'bg-teal-500' },
                    { label: '60%+ Fair',       color: 'bg-amber-500' },
                    { label: 'Below 60%',       color: 'bg-rose-400' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <div className={`w-2.5 h-2.5 rounded-full ${item.color}`} />
                      {item.label}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Prayer Companions ── */}
            {activeTab === 'companions' && (
              <div>
                <div className="mb-4 p-4 rounded-2xl bg-card border border-border shadow-card">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">🤝</span>
                    <h2 className="font-700 text-foreground text-sm">Your Prayer Companions</h2>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    People you pray alongside most. Build accountability and grow together.
                  </p>
                </div>

                <div className="grid gap-3">
                  {companions.map((companion) => (
                    <CompanionCard key={companion.user_id} companion={companion} />
                  ))}
                </div>

                {companions.length === 0 && (
                  <div className="text-center py-16 bg-card border border-border rounded-2xl">
                    <p className="text-4xl mb-3">🤝</p>
                    <p className="font-600 text-foreground mb-1">No companions yet</p>
                    <p className="text-sm text-muted-foreground mb-4">Invite friends to pray together and build streaks.</p>
                    <Link
                      href="/dashboard"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl gradient-teal text-white font-600 text-sm shadow-prayer hover:opacity-90 transition-opacity"
                    >
                      Go to Dashboard →
                    </Link>
                  </div>
                )}

                {/* Companion stats summary */}
                {companions.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    <div className="bg-card border border-border rounded-2xl p-4 text-center shadow-card">
                      <p className="text-xl font-700 text-foreground">{companions.length}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Companions</p>
                    </div>
                    <div className="bg-card border border-border rounded-2xl p-4 text-center shadow-card">
                      <p className="text-xl font-700 text-foreground">
                        {companions.reduce((s, c) => s + c.shared_prayers, 0)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">Shared Prayers</p>
                    </div>
                    <div className="bg-card border border-border rounded-2xl p-4 text-center shadow-card">
                      <p className="text-xl font-700 text-foreground">
                        {Math.max(...companions.map((c) => c.streak_together))}d
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">Longest Together</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ─── CTA ─────────────────────────────────────────────────────────── */}
        <div className="mt-10 p-6 rounded-2xl bg-card border border-border shadow-card flex flex-col sm:flex-row items-center gap-4">
          <div className="flex-1">
            <p className="font-700 text-foreground mb-1">Keep climbing the ranks 🚀</p>
            <p className="text-sm text-muted-foreground">Log your prayers daily to maintain your streak and rise in the community rankings.</p>
          </div>
          <Link
            href="/dashboard"
            className="flex-shrink-0 px-6 py-3 rounded-xl gradient-teal text-white font-600 text-sm shadow-prayer hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            Log Today's Prayers →
          </Link>
        </div>

      </div>
    </AppLayout>
  );
}
